import React from 'react'

import User from '../../models/User'

import FormPage from '../FormPage'
import ClientUsersPage from '../clients/ClientUsersList'

import Client from '../../models/Client'
import {useLocation, useNavigate} from "react-router-dom";
import {InfoCircleOutlined} from "@ant-design/icons"
import InternationPhoneInput from "../../components/form/InternationPhoneInput";
export default class InviteUser extends FormPage
{
    static path = '/org/members/invite';

	/**
	 * The css class for the page container
	 *
	 * @var {String}
	 */
	static BGClass = 'bordered-panel client-user-form';

    /**
     * The max width in pixels for the form container
     *
     * @var {Integer}
     */
    static MaxWidth = 800;

    /**
     * @var {Integer}
     */
    static IncludeExtraFields = false;

    /**
     * @returns {Object}
     */
    navBar()
    {
        return {
        	back: {
        		path: ClientUsersPage.defaultPath(),
        		title: "Client Users",
        	},
        };
    }

    /**
     * The title shown on the form
     */
	static FormTitle = "clients.create_company_user.title";

	/**
     * The api endpoint path that the form should send a post request to
     */
	static FormPost = "/api/clients/{client_id}/users/create";

	/**
     * The submit button title for the form
     */
	static FormSubmit = "clients.create_company_user.submit_button";

	userRoleOptions()
	{
		return this.context?.auth?.invitableUserRoles();
	}

	/**
     * The fields mapping for the form
     */
    formFields()
    {
    	let fields = [];

		if (this.context.auth?.clientOrg?.()?.is_supplier && this.context.auth?.canManageClientResourceTalent()) {
			fields.push({
				style: styles.fieldSection,
				fields: {
					supplier_note: (
						<div className="mx-3 p-3.5 border border-[#8563D7]" style={{background: 'rgba(133, 99, 215, 0.1)'}}>
							<p className="font-medium text-base mb-3">
								<InfoCircleOutlined className="pr-1 text-secondary"/> Platform User Accounts vs. Talent Accounts:
							</p>
							<ul className="list-disc pl-6">
								<li className="mb-2">
									<p className="text-xs">
										A platform User Account (at the admin+ level) and its associated <b>email ID</b> can operate on the buy
										side as part of finding talent and on the supply side to manage resources and opportunities.
									</p>
								</li>
								<li className="mb-2">
									<p className="text-xs">
										A Talent Account and an associated <b>email ID</b> is required to create a profile on the marketplace so
										that talent can be matched with client opportunities.
									</p>
								</li>
								<li>
									<p className="text-xs">
										The <b>email ID</b> used for a Platform User Accounts <u><b>cannot</b></u> also be used for a talent account that gets
										matched with client opportunities. This ensures that matchable agency talent is kept separate from
										any of the mechanics of managing agency resources and opps on the supply side.
									</p>
								</li>
							</ul>
						</div>
					)
				}
			})
		}
    	if (this.constructor.IncludeExtraFields) {
    		fields.push({
		    	fields: {
			    	image_path: {
			    		label: 'clients.create_company_user.picture_label',
						type: 'pixie',
						ratio: '1:1',
                        circular: false,
						required: false,
						maxWidth: 400
					},
				},
			});
    	}

    	fields.push({
            style: styles.fieldSection,
	    	fields: {
				first_name: {
					label: 'clients.create_company_user.first_name_label',
					required: true,
		            columns: 6,
		            style: styles.field,
				},
				last_name: {
					label: 'clients.create_company_user.last_name_label',
					required: true,
		            columns: 6,
		            style: styles.field,
				},
			},
		});

		let emailField = {
			label: 'clients.create_company_user.email_label',
			type: 'email',
			required: true,
            columns: 6,
            style: styles.field,
		};

		let roleField = {
			label: 'clients.create_company_user.role_label',
			type: 'select',
			options: this.userRoleOptions() || [],
			required: true,
            columns: 6,
            style: styles.field,
		};

		let supplierFields = []
		if (this.context.auth?.clientOrg?.()?.is_supplier && this.context.auth?.canManageClientResourceTalent()) {
			supplierFields.push({
				// style: styles.fieldSection,
				fields: {
					supplier_title: (
						<div>
							<p className="font-medium">
								Access to Supplier Resource Management Portal
							</p>
							<p className="text-sm mt-1">
								Set whether this user has access to the Supplier dashboard where they can invite talent resources,
								edit the organization profile, and manage/pursue jobs for talent.
							</p>
						</div>
					),
					can_manage_resource_talent: {
						label: 'Yes, this user can manage talent resources',
						required: false,
						columns: 6,
						style: styles.field,
						type: 'checkbox'
					},
				},
			})
		}
    	if (!this.constructor.IncludeExtraFields) {

    		let sectionFields = {
				email: emailField,
			};

    		if (this.userRoleOptions()) {
    			sectionFields.role = roleField;
    		}

    		return fields.concat([
				{
		            style: styles.fieldSection,
			    	fields: sectionFields,
				},
				...supplierFields,
			]);
    	}

    	fields = fields.concat([
			{
	            style: styles.fieldSection,
		    	fields: {
					email: emailField,
					phone: {
                        type: 'antd-input',
                        children: (
                            <InternationPhoneInput/>
                        ),
						label: 'clients.create_company_user.phone_label',
						required: false,
			            columns: 6,
			            style: styles.field,
					},
				},
			},
			{
	            style: styles.fieldSection,
		    	fields: {
					job_title: {
						label: 'clients.create_company_user.job_title_label',
						required: false,
			            columns: 6,
			            style: styles.field,
					},
					location: {
						label: 'clients.create_company_user.location_label',
						type: 'location',
						required: false,
			            columns: 6,
			            style: styles.field,
					},
		    	},
		    }
	    ]);

    	if (this.userRoleOptions())
    	{
    		fields.push({
	            style: styles.fieldSection,
		    	fields: {
					role: roleField,
		    	},
		    })
    	}

		fields.push(supplierFields);

    	return fields;
    }

   	/**
     * Maps form input data to form submit model
     * Appends client id to form data from auth user assigned client
     *
     * @param {Object} model
     * @returns {Object}
     */
	mapping(model)
	{
		let user = this.context.auth;

		model.client_id = user.clientOrg().id;

		if (!user.isClientAdmin() && !this.context?.auth?.isClientConsultant()) {
    		model.role = TB.reverse(laravel.client_user_roles)[Client.Roles.BaseUser];
    	}

        if (this.state.user) {
            model = {...this.state.user, ...model}
        }

		model.image = model.image_path;

		return model;
	}

	/**
     * Handles successful responses returned by TB Request
     *
     * @param {Object} responseData
     * @returns void
     */
	async onSuccess(responseData)
	{
        let user = User.make({...responseData.data.user});

        await User.clearCache();

		await TB.alert(
			'success',
			'clients.create_company_user.success.title',
			'clients.create_company_user.success.message'
		);

	    this.props.navigate({
	    	pathname: ClientUsersPage.pathFor(user.status),
            key: TB.uuid()
	    }, {replace: true});
	}

	// renderFormChildren() {
	// 	if (!this.constructor.IncludeExtraFields) return null;
	// 	const groups = this.context?.auth?.client_project_groups;
	// 	const editingUser = User.make(this.state.model)
    //     if (editingUser.role) {
    //         editingUser.role_name = TB.reverse(laravel.client_user_roles)[editingUser.role]
    //     }
    //
	// 	const usersGroups = editingUser.client_project_groups
    //
	// 	const updatePermissionValue = (group_id, checkboxData) => {
	// 		let correctGroup = usersGroups.find(g => g.id === group_id);
    //
	// 		let permissions = TB.set(correctGroup, checkboxData.name, checkboxData.checked ? 1 : 0);
    //
	// 		const groupOfChangedPermission = TB.find(TB.values(ProjectGroup.GroupedPermissions), group => TB.values(group).includes(checkboxData.name));
	// 		const canViewGroupPermission = TB.find(groupOfChangedPermission, perm => perm.includes('can_view'))
    //
	// 		// check if all boxes are unchecked - if so, remove group altogether
	// 		const determineIfAllUnchecked = Object.keys(permissions).filter(item => {
	// 			if (item.includes('can_')) {
	// 				if (permissions[item] === 0) {
	// 					return false
	// 				}
	// 				return true
	// 			}
	// 		})
    //
	// 		if (!determineIfAllUnchecked.length) {
	// 			return updateGroupToUser(permissions);
	// 		}
    //
    //
	// 		if (checkboxData.name.includes('can_view') && !checkboxData.checked) {
	// 			TB.map(groupOfChangedPermission, perm => {
	// 				permissions = TB.set(permissions, perm, 0);
	// 			})
	// 		} else if (checkboxData.checked && !permissions[canViewGroupPermission]) {
	// 			// Set GroupCanViewPermission to true if another permission in group is toggled on
	// 			permissions = TB.set(permissions, canViewGroupPermission, 1)
	// 		}
    //
	// 		const updatedModel = usersGroups.map(group => {
	// 			if (group.id === permissions.id) {
	// 				return { ...group, ...permissions }
	// 			}
	// 			return group;
	// 		})
    //
	// 		const updatedUser = User.make({ ...this.state.model, client_project_groups: updatedModel })
	// 		return this.promisedState(updatedUser);
	// 	}
    //
	// 	const updateGroupToUser = (group) => {
	// 		const findGroup = usersGroups.find(g => g.id === group.id);
    //
	// 		let updatedGroups = []
    //
	// 		if (!!findGroup) {
	// 			updatedGroups = usersGroups.filter(g => g.id !== group.id)
	// 		} else {
	// 			for (const property in group) {
	// 				if (property.includes('can_view_')) {
	// 					group[property] = 1;
	// 				} else if (property.includes('can_')) {
	// 					group[property] = 0;
	// 				}
	// 			}
	// 			updatedGroups = [...this.state.model.client_project_groups, group]
	// 		}
    //
	// 		const updatedUser = User.make({ ...this.state.model, client_project_groups: updatedGroups })
	// 		return this.promisedState({ model: updatedUser });
	// 	}
    //
    //     let canEditGroupsForUser = this.context?.auth?.canEditGroupPermissions() && this.context?.auth?.canEditUserGroupPermissions(editingUser);
	// 	return (
	// 		<div className="invite-user-project-group-container">
	// 			<p>Project Groups</p>
	// 			{groups.map(group => {
	// 				const matchedUsersGroups = usersGroups.find(g => g.id === group.id);
	// 				return (
	// 					<div className="project-group-category" key={group.id}>
	// 						<CheckBox
	// 							name={group.name}
	// 							value={matchedUsersGroups?.id === group.id}
	// 							disabled={!canEditGroupsForUser}
    //                             setValue={checkboxData => updateGroupToUser(group)}
	// 							label={group.name}
	// 							labelClassName="group-header"
	// 						/>
	// 						{matchedUsersGroups?.id === group.id && (
	// 							<div className="grouped-permissions">
	// 								{TB.map(ProjectGroup.GroupedPermissions, (perm, groupLabel) => {
	// 									return (
	// 										<div key={groupLabel}>
	// 											<div className="grouped-title">{groupLabel}</div>
	// 											{TB.map(perm, (key, label) => {
	// 												return (
	// 													<div className="single-permission" key={label}>
	// 														<CheckBox
	// 															name={key}
	// 															disabled={!canEditGroupsForUser}
	// 															value={!!matchedUsersGroups?.[key]}
	// 															setValue={checkboxData => updatePermissionValue(group.id, checkboxData)}
	// 															label={label}
	// 														/>
	// 													</div>
	// 												)
	// 											})}
	// 										</div>
	// 									)
	// 								})}
	// 							</div>
	// 						)}
    //
	// 					</div>
	// 				)
	// 			})}
	// 		</div>
	// 	)
	// }
}

const InviteClientUser = (props) => {
	const location = useLocation();
	const navigate = useNavigate();

	return (
		<InviteUser navigate={navigate} location={location}/>
	)
}

export {
	InviteClientUser
}
const styles = {
	fieldSection: {
		flexDirection: 'row'
	},
	field: {
		padding: '0 15px',
	},
};